import { useEffect, useState } from "react";
import { Instagram, Facebook } from "lucide-react";
import { site } from "../data/site";

type BeholdPost = {
  id: string;
  permalink: string;
  mediaType: "IMAGE" | "CAROUSEL_ALBUM" | "VIDEO";
  caption?: string;
  prunedCaption?: string;
  sizes: {
    small: { mediaUrl: string };
    medium: { mediaUrl: string };
    large: { mediaUrl: string };
    full: { mediaUrl: string };
  };
};

type BeholdFeed = {
  username: string;
  followersCount: number;
  posts: BeholdPost[];
};

export default function InstagramFeed({ compact = false }: { compact?: boolean }) {
  const [feed, setFeed] = useState<BeholdFeed | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(site.beholdFeedUrl)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setFeed(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading || !feed || !feed.posts || feed.posts.length === 0) return null;

  const posts = feed.posts.slice(0, 9);

  return (
    <section
      className={`${
        compact ? "pt-3 md:pt-4 pb-8 md:pb-12" : "py-8 md:py-12"
      } bg-muted/30`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {!compact && (
          <div className="text-center mb-6 md:mb-8">
            <p className="text-sm uppercase tracking-wider text-terra font-medium">
              Suivez-nous au quotidien
            </p>
            <h2 className="mt-2 font-display text-3xl md:text-5xl font-semibold text-ink">
              Le quotidien chez La La Land
            </h2>
            <p className="mt-3 text-ink-soft max-w-xl mx-auto leading-relaxed">
              Photos, ateliers, sourires : retrouvez la vie de nos crèches sur Instagram
              et Facebook.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
          {posts.map((post) => {
            const firstLine = (post.prunedCaption || post.caption || "")
              .split("\n")
              .find((line) => line.trim().length > 0)
              ?.trim();
            return (
              <a
                key={post.id}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="relative aspect-square overflow-hidden rounded-2xl group bg-muted shadow-soft-sm"
                aria-label={`Voir le post Instagram${
                  firstLine ? ` : ${firstLine.slice(0, 60)}` : ""
                }`}
              >
                <img
                  src={post.sizes.medium?.mediaUrl || post.sizes.large?.mediaUrl}
                  alt={firstLine || "La La Land Crèches"}
                  className="w-full h-full object-cover transition duration-300 group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition duration-300 flex items-end p-3 md:p-4">
                  <p className="text-white text-xs md:text-sm leading-snug line-clamp-3">
                    {firstLine}
                  </p>
                </div>
                <div className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition duration-300 shadow-soft-sm">
                  <Instagram size={14} className="text-ink" strokeWidth={1.8} />
                </div>
              </a>
            );
          })}
        </div>

        <div className="mt-10 md:mt-12 flex flex-wrap justify-center gap-3">
          <a
            href={site.socialLinks.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-ink/10 text-ink font-medium shadow-soft-sm hover:bg-honey-soft transition active:scale-[0.97]"
          >
            <Instagram size={18} strokeWidth={1.7} />
            @{feed.username}
          </a>
          <a
            href={site.socialLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-ink/10 text-ink font-medium shadow-soft-sm hover:bg-sage-soft transition active:scale-[0.97]"
          >
            <Facebook size={18} strokeWidth={1.7} />
            Facebook
          </a>
        </div>
      </div>
    </section>
  );
}
